// deps
import React, {
    ReactElement,
    KeyboardEvent,
    useState,
    FocusEventHandler,
    KeyboardEventHandler,
    ChangeEventHandler,
    useCallback,
    forwardRef,
    useRef,
    useImperativeHandle,
} from "react";
import classNames from "classnames";

// helpers
import { isEmpty } from "@core-ui/helpers/Utils";
// styles
import { withStyles } from "@core-ui/helpers/WithStyles";
import styles from "./InputField.scss";

// hooks
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";

// interfaces
import { InputFieldAttributes } from "@core-ui/interfaces/Input";

export interface InputFieldProps {
    className?: string;
    inputClassName?: string;
    labelClassName?: string;
    value?: string;
    onBlur?: (name?: string) => void;
    onChange?: (value: string, name?: string) => void;
    onUp?: (event: KeyboardEvent<HTMLInputElement>, name?: string) => void;
    onDown?: (event: KeyboardEvent<HTMLInputElement>, name?: string) => void;
    onFocus?: (name?: string) => void;
    attrs?: InputFieldAttributes;
    error?: boolean;
    inline?: boolean;
    style?: object;
    label?: string;
    setSelectionRange?: boolean;
    disabled?: boolean;
    keepLabelActive?: boolean;
}

interface InputFieldRefAttribute {
    focus: () => void;
    blur: () => void;
}

const Focusbar = withStyles(() => <span className={styles.focusBar} />, styles);

export default forwardRef<InputFieldRefAttribute, InputFieldProps>(function Input(
    { attrs = {}, ...props }: InputFieldProps,
    ref,
): ReactElement {
    const [isLabelActive, setLabelActive] = useState(props.keepLabelActive ?? false);

    const _input = useRef<HTMLInputElement>(null);
    const _inputContainer = useRef<HTMLDivElement>(null);
    const _labelRef = useRef<HTMLLabelElement>(null);

    const getInput = useCallback((): HTMLInputElement | null => _input.current, [_input]);
    const _isLabelActive = useCallback((): boolean => {
        const $input = getInput();
        return (
            !isEmpty(props.value) ||
            !isEmpty(attrs.placeholder) ||
            ($input !== null && ($input === document.activeElement || !isEmpty($input.value)))
        );
    }, [attrs.placeholder, getInput, props.value]);

    const getInputName = useCallback((): string | undefined => {
        return attrs.name;
    }, [attrs.name]);

    const getInputId = useCallback((): string | undefined => {
        return attrs.id || attrs.name;
    }, [attrs.id, attrs.name]);

    const onInputFocus = useCallback((): void => {
        setLabelActive(_isLabelActive());
        if (props.onFocus !== undefined) {
            props.onFocus(getInputName());
        }
    }, [_isLabelActive, getInputName, props]);

    const onInputBlur: FocusEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (event.target !== getInput()) {
                return;
            }

            setLabelActive(_isLabelActive());
            if (typeof props.onBlur === "function") {
                props.onBlur(getInputName());
            }
        },
        [_isLabelActive, getInput, getInputName, props],
    );

    const onKeyUp: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onUp === "function") {
                props.onUp(event, getInputName());
            }
        },
        [getInputName, props],
    );

    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onDown === "function") {
                props.onDown(event, getInputName());
            }
        },
        [getInputName, props],
    );

    const onInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            if (typeof props.onChange === "function") {
                props.onChange(event.target.value, getInputName());
            }
        },
        [getInputName, props],
    );

    const inputFieldClass = classNames(styles.inputField, props.className, {
        [styles.inputFieldInline]: props.inline,
    });

    const inputClass = classNames(styles.input, props.inputClassName, {
        [styles.inputError]: props.error,
    });

    const labelClass = classNames(styles.label, props.labelClassName, {
        [styles.labelActive]: isLabelActive,
        [styles.labelError]: props.error,
    });

    useImperativeHandle(ref, () => ({
        focus: (): void => {
            _input.current?.setAttribute("autofocus", "autofocus");
            _input.current?.focus();
        },
        blur: (): void => {
            _input.current?.blur();
        },
    }));

    useEffectOnce(() => {
        if (_input.current === document.activeElement) {
            setLabelActive(_isLabelActive());
            if (props.onFocus !== undefined) {
                props.onFocus(getInputName());
            }
        }
    });

    return (
        <div className={inputFieldClass} style={props.style} ref={_inputContainer}>
            <input
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                {...attrs}
                ref={_input}
                className={inputClass}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                value={props.value}
                id={getInputId()}
                disabled={props.disabled ?? false}
            />
            <Focusbar />
            <label className={labelClass} htmlFor={getInputId()} ref={_labelRef}>
                {props.label}
            </label>
        </div>
    );
});
